import { motion } from "framer-motion";
import { Cat, Flag } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const CATE = [
  ["Viral Feline DNA", "The internet's most proven meme engine — cats never miss."],
  ["Nine-Lives Resilience", "Survives every dip, every cycle, every doubt."],
  ["Cult Community", "A non-stop content machine that never sleeps."],
  ["Endlessly Remixable", "One cat, infinite formats, unlimited memes."],
];

const TRUMP = [
  ["Larger-Than-Life Presence", "Personality that commands every headline it touches."],
  ["Monumental Rallies", "Crowd energy that turns moments into movements."],
  ["Gold-Plated Swagger", "A winning brand that screams success."],
  ["Unapologetic Conviction", "Never backs down. Never goes quiet."],
];

function LineageCard({ title, icon: Icon, items, accent, border, delay, testId }) {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    e.currentTarget.style.setProperty("--ry", `${x * 5}deg`);
    e.currentTarget.style.setProperty("--rx", `${-y * 5}deg`);
    e.currentTarget.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
    e.currentTarget.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  const onLeave = (e) => {
    e.currentTarget.style.setProperty("--rx", "0deg");
    e.currentTarget.style.setProperty("--ry", "0deg");
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`group relative overflow-hidden rounded-2xl border ${border} bg-[#0E1015] p-7 transition-transform duration-200 ease-out [transform:perspective(900px)_rotateX(var(--rx,0deg))_rotateY(var(--ry,0deg))] sm:p-9`}
      data-testid={testId}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(340px circle at var(--mx, 50%) var(--my, 50%), rgba(246,200,95,0.10), transparent 60%)",
        }}
      />
      <div className="relative flex items-center gap-3">
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent.bg}`}>
          <Icon className={`h-5 w-5 ${accent.text}`} />
        </div>
        <h3 className="font-display text-xl font-extrabold uppercase tracking-tight sm:text-2xl">
          {title}
        </h3>
      </div>
      <ul className="relative mt-7 space-y-5">
        {items.map(([t, d]) => (
          <li key={t} className="group">
            <p className={`font-display text-base font-bold ${accent.text}`}>{t}</p>
            <p className="mt-1 text-sm leading-relaxed text-[#94A3B8]">{d}</p>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function FusionMatrix() {
  return (
    <section id="fusion" className="relative bg-[#0A0C10] py-28 sm:py-36" data-testid="fusion-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#E5A93C]"
        >
          02 / The Fusion
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.08 }}
          className="mt-5 max-w-3xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
        >
          Two unstoppable forces.
          <br />
          <span className="text-[#E5A93C]">One apex ticker.</span>
        </motion.h2>

        <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-[1fr_auto_1fr]">
          <LineageCard
            title="From $CATE"
            icon={Cat}
            items={CATE}
            accent={{ bg: "bg-[rgba(246,200,95,0.12)]", text: "text-[#F6C85F]" }}
            border="border-[rgba(229,169,60,0.35)]"
            delay={0.1}
            testId="fusion-cate-card"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
            className="flex items-center justify-center lg:flex-col"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[rgba(229,169,60,0.5)] bg-[#07080A] font-display text-3xl font-black text-[#E5A93C] shadow-[0_0_40px_rgba(229,169,60,0.25)]">
              +
            </div>
          </motion.div>

          <LineageCard
            title="From Trump"
            icon={Flag}
            items={TRUMP}
            accent={{ bg: "bg-[rgba(220,38,38,0.12)]", text: "text-[#F87171]" }}
            border="border-[rgba(220,38,38,0.35)]"
            delay={0.2}
            testId="fusion-trump-card"
          />
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 text-center font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#64748B]"
        >
          = <span className="text-[#F6C85F]">$TATE — The Apex Predator</span> (see above)
        </motion.p>
      </div>
    </section>
  );
}
